/**
 * Shared token configuration values
 * Fallback values - Real environment values are read in apps/client/src/config/env-token-config.ts
 */

// Define SubscriptionTier type explicitly to avoid import issues
type SubscriptionTier = "FREE" | "BASIC" | "PROFESSIONAL" | "ENTERPRISE";

// NOTE: These are literal default values only. Runtime values are read at the app boundary:
// - Client: apps/client/src/config/env-token-config.ts (import.meta.env.VITE_*)
// - Server: TokenConfigService (DB-backed, injected via NestJS DI)
// See ADR-001 Rule 3 (docs/adr/001-client-server-boundary.md)

/**
 * Token limits by subscription tier
 * These values are now configurable via environment variables
 * - FREE: For free registered users
 * - BASIC: First paid tier
 * - PROFESSIONAL: Professional tier
 * - ENTERPRISE: Highest tier
 */
export const TOKEN_LIMITS: Record<SubscriptionTier, number> = {
  FREE: 250,
  BASIC: 1400, // Increased from 700 to support 3+ complete job applications
  PROFESSIONAL: 2000,
  ENTERPRISE: 100_000,
};

/**
 * Token rollover configuration by subscription tier
 * When enabled, unused tokens carry over to the next billing period
 * When disabled, tokens reset to tier limit each period
 * - FREE: No rollover (lifetime tokens, not monthly)
 * - BASIC: No rollover (reset to tier limit each month)
 * - PROFESSIONAL: Rollover enabled (unused tokens carry over)
 * - ENTERPRISE: Rollover enabled (unused tokens carry over)
 */
export const TOKEN_ROLLOVER_ENABLED: Record<SubscriptionTier, boolean> = {
  FREE: false,
  BASIC: false,
  PROFESSIONAL: true,
  ENTERPRISE: true,
};

/**
 * User tier mapping to subscription tier
 * This maps the client-side UserTier enum to the database SubscriptionTier
 */
export const USER_TIER_TO_SUBSCRIPTION_TIER = {
  guest: "FREE",
  free: "FREE",
  paid_tokens: "PROFESSIONAL", // Users who buy tokens get PROFESSIONAL
  premium_basic: "BASIC", // Basic paid plan
  premium_enterprise: "ENTERPRISE", // Enterprise plan
} as const;

/**
 * Subscription tier to user tier mapping
 * Maps the database SubscriptionTier to client-side UserTier
 */
export const SUBSCRIPTION_TIER_TO_USER_TIER = {
  FREE: "free",
  BASIC: "premium_basic",
  PROFESSIONAL: "paid_tokens",
  ENTERPRISE: "premium_enterprise",
} as const;

/**
 * Token costs for different features
 * All values are configurable via environment variables
 */
export const TOKEN_COSTS = {
  // Core Features
  // RESUME_GENERATION reflects total streaming pipeline cost (Market Scout + Generate + Format)
  // Server: MARKET_SCOUT(100) + GENERATE_RESUME(100) + FORMAT_RESUME(50) = 250
  RESUME_GENERATION: 250,
  COVER_LETTER: 300,
  ADVANCED_RESUME_ANALYSIS: 150,

  // Analytics & Scoring
  ANALYTICS: 100,
  JOB_FIT_SCORE: 100,

  // Enhancement Features
  BOOSTER: 200,
  LINKEDIN_REWRITE: 200,
  TRAJECTORY: 100,
  LOCALIZATION: 100,

  // Export Features
  PDF_EXPORT: 25,
  WORD_EXPORT: 40,

  // AI Writing Assistant
  AI_SUMMARY: 30, // Unified summary generation cost
  SUMMARY_GENERATION: 30, // Alias for backward compatibility
  IMPROVE_WRITING: 10,
  FIX_SPELLING_GRAMMAR: 5,
  CHANGE_TONE: 10,
  ADD_METRICS: 10,
  MAKE_CONCISE: 5,
  RESUME_ROAST: 150,
};

/**
 * Initial token balances for different user types
 * These values are configurable via environment variables
 */
export const DEFAULT_GUEST_TOKEN_BALANCE = 250;
export const DEFAULT_REGISTERED_TOKEN_BALANCE = 550;

/**
 * Resume limits by subscription tier
 * These values are now configurable via environment variables
 */
export const RESUME_LIMITS: Record<SubscriptionTier, number> = {
  FREE: 2, // Registered free users
  BASIC: 5, // Basic subscription
  PROFESSIONAL: 10, // Professional subscription
  ENTERPRISE: 999, // Enterprise: Unlimited (high limit)
};

/**
 * Resume limit for guest users (not tied to subscription)
 */
export const GUEST_RESUME_LIMIT = 1;

/**
 * User Tiers - for client-side usage
 */
export enum UserTier {
  GUEST = "guest",
  FREE = "free",
  PAID_TOKENS = "paid_tokens", // PROFESSIONAL tier (for token purchasers)
  PREMIUM_BASIC = "premium_basic", // BASIC tier
  PREMIUM_ENTERPRISE = "premium_enterprise", // ENTERPRISE tier
  PREMIUM_PRO = "premium_pro", // For backward compatibility
}

/**
 * Monthly token limits by user tier
 */
export const USER_MONTHLY_TOKEN_LIMITS = {
  [UserTier.GUEST]: 0, // Guest users use their token balance
  [UserTier.FREE]: 0, // Free users use their token balance
  [UserTier.PAID_TOKENS]: TOKEN_LIMITS["PROFESSIONAL"], // Token purchasers (PROFESSIONAL tier)
  [UserTier.PREMIUM_BASIC]: TOKEN_LIMITS["BASIC"], // Basic package
  [UserTier.PREMIUM_ENTERPRISE]: TOKEN_LIMITS["ENTERPRISE"], // Enterprise package
  [UserTier.PREMIUM_PRO]: TOKEN_LIMITS["PROFESSIONAL"], // For backward compatibility
};

/**
 * Feature permissions based on user tiers
 */
export const TIER_PERMISSIONS = {
  [UserTier.GUEST]: {
    canGenerateResume: true,
    canPreviewTemplates: true,
    canExportPDF: false,
    canExportWord: false,
    canAccessPremiumFeatures: false,
    canGenerateCoverLetter: false,
    canSeeJobFitScore: false,
    canUseBooster: false,
    canUseTrajectory: false,
    canUseLocalization: false,
    canUseLinkedInRewrite: false,
    canUseAdvancedAnalytics: false,
    maxResumes: GUEST_RESUME_LIMIT,
  },
  [UserTier.FREE]: {
    canGenerateResume: true,
    canPreviewTemplates: true,
    canExportPDF: true, // Available but costs tokens
    canExportWord: true, // Available but costs tokens
    canAccessPremiumFeatures: false,
    canGenerateCoverLetter: true, // Available but costs tokens
    canSeeJobFitScore: true, // Available but costs tokens
    canUseBooster: true, // Available but costs tokens
    canUseTrajectory: true, // Available but costs tokens
    canUseLocalization: true, // Available but costs tokens
    canUseLinkedInRewrite: true, // Available but costs tokens
    canUseAdvancedAnalytics: true, // Available but costs tokens
    maxResumes: RESUME_LIMITS["FREE"],
  },
  [UserTier.PAID_TOKENS]: {
    canGenerateResume: true,
    canPreviewTemplates: true,
    canExportPDF: true,
    canExportWord: true,
    canAccessPremiumFeatures: true,
    canGenerateCoverLetter: true,
    canSeeJobFitScore: true,
    canUseBooster: true,
    canUseTrajectory: true,
    canUseLocalization: true,
    canUseLinkedInRewrite: true,
    canUseAdvancedAnalytics: true,
    maxResumes: RESUME_LIMITS["PROFESSIONAL"], // Token purchasers get professional limits
  },
  [UserTier.PREMIUM_BASIC]: {
    canGenerateResume: true,
    canPreviewTemplates: true,
    canExportPDF: true,
    canExportWord: true,
    canAccessPremiumFeatures: true,
    canGenerateCoverLetter: true,
    canSeeJobFitScore: true,
    canUseBooster: true,
    canUseTrajectory: true,
    canUseLocalization: true,
    canUseLinkedInRewrite: true,
    canUseAdvancedAnalytics: true,
    maxResumes: RESUME_LIMITS["BASIC"],
  },
  [UserTier.PREMIUM_PRO]: {
    canGenerateResume: true,
    canPreviewTemplates: true,
    canExportPDF: true,
    canExportWord: true,
    canAccessPremiumFeatures: true,
    canGenerateCoverLetter: true,
    canSeeJobFitScore: true,
    canUseBooster: true,
    canUseTrajectory: true,
    canUseLocalization: true,
    canUseLinkedInRewrite: true,
    canUseAdvancedAnalytics: true,
    maxResumes: RESUME_LIMITS["PROFESSIONAL"],
  },
  [UserTier.PREMIUM_ENTERPRISE]: {
    canGenerateResume: true,
    canPreviewTemplates: true,
    canExportPDF: true,
    canExportWord: true,
    canAccessPremiumFeatures: true,
    canGenerateCoverLetter: true,
    canSeeJobFitScore: true,
    canUseBooster: true,
    canUseTrajectory: true,
    canUseLocalization: true,
    canUseLinkedInRewrite: true,
    canUseAdvancedAnalytics: true,
    maxResumes: RESUME_LIMITS["ENTERPRISE"],
  },
};
